import type { CacheConfig } from '@/types/shared/caching.types'
import { PLATFORM_CACHE_CONFIG } from './caching/platform.caching.config'
import { ORGANIZATION_CACHE_CONFIG } from './caching/organization.caching.config'
import { BRANCH_CACHE_CONFIG } from './caching/branch.caching.config'

/**
 * Cache configuration for all pages/modules, aggregated by scope
 */
export const CACHE_CONFIG: Record<string, CacheConfig> = {
  ...PLATFORM_CACHE_CONFIG,
  ...ORGANIZATION_CACHE_CONFIG,
  ...BRANCH_CACHE_CONFIG,
}

/**
 * Get cache configuration for a specific page/module
 * 
 * @param key - The cache key (e.g., 'register.organization', 'users.list')
 * @returns Cache configuration or default config if not found
 */
export function getCacheConfig(key: string): CacheConfig {
  const config = CACHE_CONFIG[key]
  
  if (!config) {
    return {
      autosave: false,
      apiCache: false,
      frontendTTL: 5 * 60 * 1000,
      backendTTL: 0,
      storageType: 'localStorage',
    }
  }
  
  return config
}

/**
 * Get all cache configurations (for debugging/admin)
 */
export function getAllCacheConfigs(): Record<string, CacheConfig> {
  return CACHE_CONFIG
}

/**
 * Check if a module requires autosave
 */
export function requiresAutosave(key: string): boolean {
  return getCacheConfig(key).autosave
}

/**
 * Check if a module should cache API responses
 */
export function shouldCacheAPI(key: string): boolean {
  return getCacheConfig(key).apiCache
}

/**
 * Get storage key for autosave
 * Format: autosave::<orgSlug>::<module>::<subPage>
 */
export function getAutosaveKey(orgSlug: string, module: string, subPage: string): string {
  return `autosave::${orgSlug}::${module}::${subPage}`
}

/**
 * Get storage key for API cache
 * Format: cache::<orgSlug>::<module>::<subPage>
 */
export function getCacheKey(orgSlug: string, module: string, subPage: string): string {
  return `cache::${orgSlug}::${module}::${subPage}`
}

/**
 * Smart Cache Invalidation Utility
 * 
 * This utility automatically invalidates related queries based on cache config keys
 * without needing to manually specify query keys in each page.
 * 
 * @example Basic Usage in a Component:
 * ```tsx
 * import { useCacheInvalidation } from '@/hooks/useCacheInvalidation'
 * 
 * const { invalidateOnMutation } = useCacheInvalidation()
 * 
 * const deleteMutation = useMutation({
 *   mutationFn: deleteItem,
 *   onSuccess: () => {
 *     // Automatically invalidates all related queries
 *     invalidateOnMutation('academic.departments')
 *   }
 * })
 * ```
 * 
 * @example With Entity ID (for detail pages):
 * ```tsx
 * const updateMutation = useMutation({
 *   mutationFn: updateDepartment,
 *   onSuccess: () => {
 *     // Invalidates both list and detail queries
 *     invalidateOnMutation('academic.departments', departmentId)
 *   }
 * })
 * ```
 * 
 * @example With Scope:
 * ```tsx
 * // For scoped entities like groups
 * invalidateOnMutation('groups.list', undefined, { scope: 'platform' })
 * ```
 * 
 * @example Multiple Cache Keys:
 * ```tsx
 * const { invalidateMultiple } = useCacheInvalidation()
 * 
 * // When one operation affects multiple entities
 * invalidateMultiple(['academic.departments', 'academic.classes'])
 * ```
 */

import { QueryClient } from '@tanstack/react-query'

/**
 * Maps cache config keys to their query key patterns
 * This allows automatic inference of query keys from cache config keys
 */
const CACHE_KEY_TO_QUERY_PATTERNS: Record<string, string[][]> = {
  // Platform
  'users.list': [['users'], ['users', 'list']],
  'groups.list': [['groups'], ['groups', 'list']],
  'roles.list': [['roles'], ['roles', 'list']],
  'features.list': [['features'], ['features', 'list']],
  'carousels.list': [['carousels'], ['carousels', 'list']],
  'coupons.list': [['coupons', 'list']],
  'plans.list': [['plans']],
  'form-management.list': [['form-management'], ['form-management', 'list']],
  'feature-flags.list': [['feature-flags'], ['feature-flags', 'list']],
  'menu.list': [['menus'], ['menus', 'list']],
  'menu.sidebar': [['menu', 'sidebar'], ['menus'], ['menus', 'list']],
  'dashboard-widgets.list': [['platform-widgets'], ['dashboard-widgets']],
  'pilot-access.list': [['pilot-access'], ['pilot-access', 'list']],

  // Organization — include singular detail prefix so invalidateOnMutation(slug) hits ['organization-branch', slug]
  // (stripping 's' from 'organization-branches' wrongly yields 'organization-branche')
  'organization.branches': [
    ['organization-branches'],
    ['organization-branch'],
    ['organization', 'branches'],
  ],
  'organization.roles': [['organization-roles'], ['organization-role']],
  'organization.groups': [['organization-groups'], ['organization-group']],
  
  // Branch (roles & groups - list + detail for cache invalidation)
  'branch.roles': [['branch-roles'], ['branch-roles', 'list'], ['branch-role']],
  'branch.groups': [['branch-groups'], ['branch-groups', 'list'], ['branch-group'], ['branch-group-roles']],

  // Branch/Academic
  'academic.departments': [['departments']],
  'academic.classes': [['classes']],
  'academic.subjects': [['subjects']],
  // All keys used for academic year lists/dropdowns across the app (so delete/update clears every dropdown)
  'academic.academic-years': [
    ['academic-years'],
    ['academic-years-list'],
    ['all-academic-years'],
    ['branch-academic-years'],
  ],
  'academic.terms': [['terms']],
  'academic.term-detail': [['term']],
  'academic.sections': [['sections']],
  'academic.department-detail': [['department']],
  
  // Add more mappings as needed
}

/**
 * Infers query key patterns from cache config key
 * Handles common patterns like 'module.entity' -> ['entity'] or ['module', 'entity']
 */
function inferQueryKeyPatterns(cacheKey: string): string[][] {
  // If explicit mapping exists, use it
  if (CACHE_KEY_TO_QUERY_PATTERNS[cacheKey]) {
    return CACHE_KEY_TO_QUERY_PATTERNS[cacheKey]
  }

  // Try to infer from cache config
  const config = getCacheConfig(cacheKey)
  if (config.queryKey) {
    return [config.queryKey]
  }

  // Infer from cache key pattern: 'module.entity' -> ['entity'] and ['module', 'entity']
  const parts = cacheKey.split('.')
  if (parts.length >= 2) {
    const entity = parts[parts.length - 1]
    const module = parts[parts.length - 2]
    
    // Common patterns
    if (entity === 'list') {
      const baseEntity = parts.length > 2 ? parts[parts.length - 2] : module
      return [
        [baseEntity],
        [baseEntity, 'list'],
        [module, baseEntity],
        [module, baseEntity, 'list'],
      ]
    }
    
    return [
      [entity],
      [module, entity],
      [entity, 'list'],
      [module, entity, 'list'],
    ]
  }

  // Fallback: use the last part as query key
  return [[parts[parts.length - 1]]]
}

/**
 * Smart cache invalidation based on cache config key
 * Automatically invalidates all related queries without manual query key specification
 * 
 * @param queryClient - React Query client instance
 * @param cacheConfigKey - Cache config key (e.g., 'academic.departments', 'groups.list')
 * @param options - Additional invalidation options
 */
export function invalidateCache(
  queryClient: QueryClient,
  cacheConfigKey: string,
  options?: {
    exact?: boolean
    alsoInvalidate?: string[][] // Additional query keys to invalidate
    scope?: string // Scope-specific invalidation (e.g., 'platform', 'organization', 'branch')
  }
): void {
  const queryPatterns = inferQueryKeyPatterns(cacheConfigKey)
  const allPatterns = [...queryPatterns, ...(options?.alsoInvalidate || [])]

  // Invalidate each pattern
  allPatterns.forEach(pattern => {
    // If scope is provided, add it to the pattern
    const queryKey = options?.scope ? [...pattern, options.scope] : pattern
    
    queryClient.invalidateQueries({
      queryKey,
      exact: options?.exact ?? false, // Default to false to catch all variations
    })
  })
}

/**
 * Invalidate cache for create/update/delete operations
 * This is a convenience wrapper that invalidates list and detail caches
 * 
 * @param queryClient - React Query client instance
 * @param cacheConfigKey - Cache config key for the entity
 * @param entityId - Optional entity ID for detail cache invalidation
 * @param options - Additional options
 */
export function invalidateCacheOnMutation(
  queryClient: QueryClient,
  cacheConfigKey: string,
  entityId?: string | number,
  options?: {
    scope?: string
    alsoInvalidate?: string[][]
  }
): void {
  // Invalidate list queries
  invalidateCache(queryClient, cacheConfigKey, {
    exact: false,
    scope: options?.scope,
    alsoInvalidate: options?.alsoInvalidate,
  })

  // If entity ID provided, also invalidate detail queries
  if (entityId) {
    const detailPatterns = inferQueryKeyPatterns(cacheConfigKey)
    detailPatterns.forEach(pattern => {
      // Convert list patterns to detail patterns
      // e.g., ['departments'] -> ['department', entityId]
      const entityName = pattern[pattern.length - 1]
      const detailKey = entityName.endsWith('s') 
        ? entityName.slice(0, -1) // Remove 's' from plural
        : entityName
      
      queryClient.invalidateQueries({
        queryKey: [detailKey, entityId],
        exact: false,
      })
    })
  }
}

/**
 * Batch invalidate multiple cache config keys at once
 * Useful when one operation affects multiple entities
 */
export function invalidateMultipleCaches(
  queryClient: QueryClient,
  cacheConfigKeys: string[],
  options?: {
    scope?: string
  }
): void {
  cacheConfigKeys.forEach(key => {
    invalidateCache(queryClient, key, {
      exact: false,
      scope: options?.scope,
    })
  })
}

